import type * as THREE from 'three';

export type Vector3Like = { x: number; y: number; z: number };

/** Collider behavior: solid = camera stays outside, hollow = camera stays inside */
export type ColliderMode = 'solid' | 'hollow';

export interface BoxColliderDef {
  id: string;
  type: 'box';
  /**
   * - `'solid'`: camera stays OUTSIDE the box (preferred for obstacles).
   * - `'hollow'`: camera stays INSIDE the box (закрытое помещение — стены/пол/потолок).
   *   Hollow boxes are AABB only; `rotation` is ignored.
   */
  mode: 'solid' | 'hollow';
  enabled: boolean;
  /** When false, this collider is ignored during line-of-sight checks (default true) */
  blocksLineOfSight?: boolean;
  position: Vector3Like;
  /** Full dimensions (width, height, depth) */
  size: Vector3Like;
  /**
   * Optional Euler rotation (radians, XYZ order). When omitted, the box behaves as
   * an axis-aligned bounding box. When set, the collider becomes an oriented
   * bounding box (OBB) — its faces rotate, and both collision resolution and
   * line-of-sight tests are performed in the box's local space. **Ignored when
   * `mode === 'hollow'`**.
   */
  rotation?: Vector3Like;
}

export interface CylinderColliderDef {
  id: string;
  type: 'cylinder';
  mode: 'hollow';
  enabled: boolean;
  /** When false, this collider is ignored during line-of-sight checks (default true) */
  blocksLineOfSight?: boolean;
  position: Vector3Like;
  radius: number;
  height: number;
}

export interface SphereColliderDef {
  id: string;
  type: 'sphere';
  mode: 'solid';
  enabled: boolean;
  /** When false, this collider is ignored during line-of-sight checks (default true) */
  blocksLineOfSight?: boolean;
  position: Vector3Like;
  radius: number;
}

export type ColliderDef = BoxColliderDef | CylinderColliderDef | SphereColliderDef;

export interface ColliderSceneConfig {
  cameraRadius: number;
  enabled: boolean;
  epsilon: number;
  colliders: ColliderDef[];
}

/** Legacy collision config used by Leva panel */
export interface CollisionConfig {
  cameraRadius: number;
  maxDistance: number;
  enabled: boolean;
  epsilon: number;
}

export const defaultCollisionConfig: CollisionConfig = {
  cameraRadius: 0.05,
  maxDistance: 1.5,
  enabled: true,
  epsilon: 0.0001,
};

export interface CollisionOptions {
  /** Enable surface sliding along collision planes (default: true).
   *  Disable for orbit-style controls where the controller already provides angular movement. */
  slide?: boolean;
}

/** Minimal interface consumed by controls and PoiMarkerManager */
export interface ICollisionSystem {
  getSafePosition(
    currentPosition: THREE.Vector3,
    desiredPosition: THREE.Vector3,
    options?: CollisionOptions,
  ): THREE.Vector3;

  isLineOfSightObstructed(
    start: THREE.Vector3,
    target: THREE.Vector3,
    padding?: number,
  ): boolean;

  getConfig(): { epsilon: number; enabled: boolean; cameraRadius: number };
}
